import React from 'react';
import Box from '../../components/Common/Box';

interface LeaderboardEntry {
    rank: number | null;
    pseudonym: string | null;
    twr_performance: number;
    calculated_at: string;
    entity_type?: string;
    entity_id?: string;
    is_benchmark?: boolean;
}

interface MobileLeaderboardProps {
    activeTab: 'LOCAL' | 'GLOBAL';
    setActiveTab: (tab: 'LOCAL' | 'GLOBAL') => void;
    period: 'TODAY' | 'WEEKLY' | 'MONTHLY' | 'LAST_MONTH' | 'YTD';
    setPeriod: (period: 'TODAY' | 'WEEKLY' | 'MONTHLY' | 'LAST_MONTH' | 'YTD') => void;
    loading: boolean;
    localData: any[];
    globalData: LeaderboardEntry[];
    myRank: { rank: number | null; twr_performance: number | null; pseudonym: string | null; exclusion: string | null } | null;
    hasOptedIn: boolean;
    user: any;
}

const MobileLeaderboard: React.FC<MobileLeaderboardProps> = ({
    activeTab, setActiveTab, period, setPeriod, loading, localData, globalData, myRank, hasOptedIn, user
}) => {
    return (
        <div style={{ paddingBottom: '30px' }}>

            {/* --- HEADER KPI (Sticky Top) --- */}
            <div style={{ padding: '24px 20px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '8px' }}>
                    {activeTab === 'LOCAL' ? '🏅' : '🌍'}
                </div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', color: 'var(--text-heading)', fontWeight: '800' }}>
                    {activeTab === 'LOCAL' ? 'Comparatif Privé' : 'Classement Général'}
                </h2>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', lineHeight: '1.4' }}>
                    {activeTab === 'LOCAL'
                        ? 'Mesurez-vous face à Nia et aux indices.'
                        : 'Comparez vos rendements avec la communauté.'}
                </div>
            </div>

            {/* --- SEGMENTED CONTROL --- */}
            <div style={{ padding: '16px 20px', position: 'sticky', top: '0', background: 'var(--bg-main)', zIndex: 10, borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', background: 'var(--bg-input)', borderRadius: '12px', padding: '4px', marginBottom: '12px' }}>
                    <button
                        onClick={() => setActiveTab('LOCAL')}
                        style={{
                            flex: 1, padding: '12px 0', borderRadius: '10px', fontSize: '13px', fontWeight: '600',
                            background: activeTab === 'LOCAL' ? '#1e40af' : 'transparent',
                            color: activeTab === 'LOCAL' ? '#ffffff' : 'var(--text-muted)',
                            border: 'none', boxShadow: activeTab === 'LOCAL' ? '0 2px 4px rgba(30, 64, 175, 0.3)' : 'none',
                            transition: 'all 0.2s', touchAction: 'manipulation'
                        }}
                    >
                        Privé
                    </button>
                    <button
                        onClick={() => setActiveTab('GLOBAL')}
                        style={{
                            flex: 1, padding: '12px 0', borderRadius: '10px', fontSize: '13px', fontWeight: '600',
                            background: activeTab === 'GLOBAL' ? '#1e40af' : 'transparent',
                            color: activeTab === 'GLOBAL' ? '#ffffff' : 'var(--text-muted)',
                            border: 'none', boxShadow: activeTab === 'GLOBAL' ? '0 2px 4px rgba(30, 64, 175, 0.3)' : 'none',
                            transition: 'all 0.2s', touchAction: 'manipulation'
                        }}
                    >
                        Général
                    </button>
                </div>

                {/* Period Selector Native Full-Width for easy tap */}
                <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value as any)}
                    style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-box)',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--text-main)',
                        appearance: 'none', // to make it look cleaner, though native on iOS hides it anyway
                        WebkitAppearance: 'none',
                        outline: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 16px center',
                        backgroundSize: '16px'
                    }}
                >
                    <>
                        <option value="TODAY">Aujourd'hui</option>
                        <option value="WEEKLY">Cette Semaine</option>
                        <option value="MONTHLY">Ce Mois-ci</option>
                        <option value="LAST_MONTH">Mois dernier</option>
                        <option value="YTD">Depuis le 1er Janvier (YTD)</option>
                    </>
                </select>
            </div>

            <div style={{ padding: '16px 20px' }}>
                {!hasOptedIn && activeTab === 'GLOBAL' && (
                    <div style={{ backgroundColor: '#fffaf0', border: '1px solid #feebc8', color: '#dd6b20', padding: '16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', lineHeight: '1.5' }}>
                        <strong>⚠️ Non inscrit</strong><br />
                        Vous n'êtes pas inscrit au classement public. Définissez un pseudo dans votre profil.
                    </div>
                )}

                {!loading && myRank?.exclusion && (
                    <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', lineHeight: '1.5' }}>
                        {myRank.exclusion}
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}><div className="loading-spinner" /><br />Calcul en cours...</div>
                ) : activeTab === 'LOCAL' ? (
                    localData.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Aucun classement local disponible.</div>
                    ) : (() => {
                        const sorted = [...localData].sort((a, b) => b.twr_performance - a.twr_performance);
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {sorted.map((item, index) => {
                                    const isUser = item.entity_type === 'USER';
                                    const isAI = item.entity_type === 'AI';
                                    const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                                    const perf = item.twr_performance;
                                    const isPositive = perf >= 0;

                                    return (
                                        <Box key={index} style={{
                                            borderLeft: isUser ? '4px solid #3b82f6' : 'none',
                                            backgroundColor: isUser ? 'var(--bg-input)' : 'var(--bg-box)'
                                        }}>
                                            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                {/* Left: Rank & Info */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ fontSize: medal ? '24px' : '16px', fontWeight: '800', color: 'var(--text-muted)', width: '32px', textAlign: 'center' }}>
                                                        {medal || `#${index + 1}`}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '16px', fontWeight: isUser ? 'bold' : '600', color: isUser ? '#3b82f6' : 'var(--text-heading)' }}>
                                                            {isUser ? 'Mon Portefeuille' : item.entity_id}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                                            {isUser ? '🧑‍💼 Vous' : isAI ? '🤖 IA Alpha' : '📈 Indice Marché'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Performance */}
                                                <div style={{
                                                    fontSize: '15px', fontWeight: 'bold',
                                                    color: isPositive ? '#16a34a' : '#dc2626',
                                                    backgroundColor: isPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                                                    padding: '6px 12px', borderRadius: '8px'
                                                }}>
                                                    {isPositive ? '+' : ''}{perf.toFixed(2)}%
                                                </div>
                                            </div>
                                        </Box>
                                    );
                                })}
                            </div>
                        );
                    })()
                ) : (
                    globalData.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Le classement est vide ou en calcul.</div>
                    ) : (
                        <>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                🏆 Top {Math.min(globalData.length, 5)} — {globalData.length} participant{globalData.length > 1 ? 's' : ''}
                            </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {globalData.slice(0, 5).map((item, index) => {
                                const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                                const perf = item.twr_performance;
                                const isMe = item.pseudonym === user?.social_profile?.pseudonym;
                                const displayName = item.pseudonym && item.pseudonym.length > 40 ? item.pseudonym.slice(0, 40) + '…' : (item.pseudonym || 'Utilisateur Anonyme');
                                const isPositive = perf >= 0;

                                return (
                                    <Box key={index} style={{
                                        borderLeft: isMe ? '4px solid #3b82f6' : 'none',
                                        backgroundColor: isMe ? 'var(--bg-input)' : 'var(--bg-box)'
                                    }}>
                                        <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            {/* Left: Rank & Info */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: medal ? '24px' : '16px', fontWeight: '800', color: 'var(--text-muted)', width: '32px', flexShrink: 0, textAlign: 'center' }}>
                                                    {medal || `#${index + 1}`}
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontSize: '16px', fontWeight: isMe ? 'bold' : '600', color: isMe ? '#3b82f6' : 'var(--text-heading)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                        {displayName}
                                                    </div>
                                                    {isMe && <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px', fontWeight: 'bold' }}>Vous</div>}
                                                </div>
                                            </div>

                                            {/* Right: Performance */}
                                            <div style={{
                                                fontSize: '15px', fontWeight: 'bold',
                                                color: isPositive ? '#16a34a' : '#dc2626',
                                                backgroundColor: isPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                                                padding: '6px 12px', borderRadius: '8px',
                                                flexShrink: 0, marginLeft: '8px'
                                            }}>
                                                {isPositive ? '+' : ''}{perf.toFixed(2)}%
                                            </div>
                                        </div>
                                    </Box>
                                );
                            })}
                        </div>
                        {/* Rang du user s'il est hors top 10 */}
                        {(() => {
                            const isMeInTop5 = globalData.slice(0, 5).some(item => item.pseudonym === user?.social_profile?.pseudonym);
                            if (!isMeInTop5 && myRank && myRank.rank) {
                                const perf = myRank.twr_performance!;
                                const displayName = myRank.pseudonym && myRank.pseudonym.length > 40 ? myRank.pseudonym.slice(0, 40) + '…' : (myRank.pseudonym || 'Utilisateur Anonyme');
                                const isPositive = perf >= 0;

                                // Calculate virtual rank including index
                                const indexItem = globalData.find(item => item.is_benchmark);
                                const virtualRank = (indexItem && indexItem.twr_performance > (myRank.twr_performance || 0))
                                    ? myRank.rank + 1
                                    : myRank.rank;

                                return (
                                    <>
                                        <div style={{ textAlign: 'center', padding: '4px', color: 'var(--text-muted)', fontSize: '13px' }}>•••</div>
                                        <Box style={{ borderLeft: '4px solid #3b82f6', backgroundColor: 'var(--bg-input)' }}>
                                            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-muted)', width: '32px', flexShrink: 0, textAlign: 'center' }}>#{virtualRank}</div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#3b82f6', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{displayName}</div>
                                                        <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px', fontWeight: 'bold' }}>Vous</div>
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '15px', fontWeight: 'bold', color: isPositive ? '#16a34a' : '#dc2626', backgroundColor: isPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)', padding: '6px 12px', borderRadius: '8px', flexShrink: 0, marginLeft: '8px' }}>
                                                    {isPositive ? '+' : ''}{perf.toFixed(2)}%
                                                </div>
                                            </div>
                                        </Box>
                                    </>
                                );
                            }
                            return null;
                        })()}
                        </>
                    )
                )}
            </div>
        </div>
    );
};

export default MobileLeaderboard;
